import type { JSX } from 'react';
import { cn } from '@/lib/utils';

/**
 * F5-T7 · Piezas compartidas de la tanda del AGENTE (contratos Agente-01/02/03).
 *
 * - LangSegment: el conmutador segmentado ES/EN de Pre-llamada y Screening
 *   (contenedor gris, segmento activo navy) — distinto A PROPÓSITO del
 *   LangToggle gold del chrome del navigator: el dibujo del agente lo declara
 *   segmentado, y define el idioma del guion Y de la interfaz a la vez.
 * - Meter: la barra de progreso de 8px del contrato (track hair, fill por
 *   tono, valor SIEMPRE en texto al lado — el caller lo pone).
 */

export function LangSegment<T extends string>({
  value,
  options,
  onPick,
  className,
}: {
  value: T;
  options: Array<{ v: T; label: string }>;
  onPick: (v: T) => void;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-[2px] bg-nav-soft border-[1.5px] border-nav-card rounded-lg p-[3px]',
        className,
      )}
      role="group"
    >
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onPick(o.v)}
          aria-pressed={value === o.v}
          className={cn(
            'min-h-[44px] px-3.5 rounded-lg font-head font-bold text-[12.5px] transition-colors cursor-pointer',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal',
            value === o.v
              ? 'bg-nav-navy text-white hover:bg-nav-navyHover'
              : 'bg-transparent text-nav-slate hover:bg-white hover:text-nav-navy',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Meter({
  pct,
  tone = 'teal',
  className,
}: {
  pct: number;
  /** teal = en curso · sage = meta cumplida · plata = swatch del tier. */
  tone?: 'teal' | 'sage' | 'plata';
  className?: string;
}): JSX.Element {
  const fill =
    tone === 'sage' ? 'bg-nav-sage' : tone === 'plata' ? 'bg-[#8fc3c4]' : 'bg-nav-teal';
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className={cn('h-2 rounded bg-nav-hair overflow-hidden', className)} aria-hidden="true">
      <div
        className={cn('h-full rounded transition-[width] duration-300 ease-[cubic-bezier(.4,0,.2,1)]', fill)}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

/** «2:41» — horas:minutos con tabular-nums (tiempo en llamada del día). */
export function fmtHoursMinutes(totalSec: number): string {
  const min = Math.floor(totalSec / 60);
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

/** Edad en años desde un DOB ISO (yyyy-mm-dd). Null si no se puede. */
export function ageFromDob(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const beforeBirthday =
    now.getMonth() < d.getMonth() ||
    (now.getMonth() === d.getMonth() && now.getDate() < d.getDate());
  if (beforeBirthday) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}
